import { getPartnerDateInputError, isValidPartnerDateInput } from './partnerDateUtils';

export const isValidProjectLeadEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  const trimmed = email.trim();
  return (
    trimmed.includes('@')
    && trimmed.includes('.')
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)
  );
};

export const getProjectLeadEmailError = (email) => {
  if (!email || String(email).trim() === '') {
    return 'This field is required';
  }

  if (!isValidProjectLeadEmail(email)) {
    return 'Enter a valid email with @ and . (e.g. name@company.com)';
  }

  return '';
};

const hasInputValue = (value) => {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  return true;
};

export const validatePartnerProjectPayload = (payload) => {
  const errors = {};
  const requiredFields = [
    'partnerName',
    'csn',
    'customerName',
    'projectLeadName',
    'projectLeadEmail',
    'engagementType',
    'deploymentModel',
    'projectStatus',
    'estimatedStartDate',
    'estimatedGoliveDate',
    'projectDescription',
  ];

  requiredFields.forEach((field) => {
    if (!payload[field] || String(payload[field]).trim() === '') {
      errors[field] = 'This field is required';
    }
  });

  if (!Array.isArray(payload.productsInScope) || payload.productsInScope.length === 0) {
    errors.productsInScope = 'Select at least one product';
  }

  const emailError = getProjectLeadEmailError(payload.projectLeadEmail);
  if (emailError) {
    errors.projectLeadEmail = emailError;
  }

  ['estimatedStartDate', 'estimatedGoliveDate'].forEach((field) => {
    if (errors[field]) {
      return;
    }

    const dateError = getPartnerDateInputError(payload[field]);
    if (dateError) {
      errors[field] = dateError;
    }
  });

  if (
    payload.estimatedStartDate
    && payload.estimatedGoliveDate
    && isValidPartnerDateInput(payload.estimatedStartDate)
    && isValidPartnerDateInput(payload.estimatedGoliveDate)
    && payload.estimatedGoliveDate < payload.estimatedStartDate
  ) {
    errors.estimatedGoliveDate = 'Go-live date must be on or after start date';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const omitEmptyProjectUpdateFields = (payload) => {
  const result = { ...payload };
  const nullableClearableFields = new Set(['actualStartDate', 'actualGoliveDate']);

  Object.keys(result).forEach((key) => {
    const value = result[key];
    if (value === null && nullableClearableFields.has(key)) {
      return;
    }
    if (value === null || value === undefined) {
      delete result[key];
      return;
    }
    if (typeof value === 'string' && value.trim() === '') {
      delete result[key];
      return;
    }
    if (Array.isArray(value) && value.length === 0) {
      delete result[key];
    }
  });

  return result;
};

const validateOptionalDateFields = (payload, errors, fields) => {
  fields.forEach((field) => {
    if (!(field in payload) || errors[field]) {
      return;
    }

    const value = payload[field];
    if (!hasInputValue(value)) {
      return;
    }

    const dateError = getPartnerDateInputError(value);
    if (dateError) {
      errors[field] = dateError === 'This field is required'
        ? 'Enter a valid date as MM/DD/YYYY'
        : dateError;
    }
  });
};

export const validatePartnerProjectUpdatePayload = (payload) => {
  const errors = {};

  if (hasInputValue(payload.projectLeadEmail) && !isValidProjectLeadEmail(payload.projectLeadEmail)) {
    errors.projectLeadEmail = 'Enter a valid email with @ and . (e.g. name@company.com)';
  }

  if ('productsInScope' in payload && Array.isArray(payload.productsInScope) && payload.productsInScope.length > 0) {
    // Non-empty product selection is valid; no further checks needed.
  }

  ['estimatedStartDate', 'estimatedGoliveDate'].forEach((field) => {
    if (!(field in payload) || errors[field]) {
      return;
    }

    const value = payload[field];
    if (!hasInputValue(value)) {
      return;
    }

    const dateError = getPartnerDateInputError(value);
    if (dateError) {
      errors[field] = dateError === 'This field is required'
        ? 'Enter a valid date as MM/DD/YYYY'
        : dateError;
    }
  });

  validateOptionalDateFields(payload, errors, ['actualStartDate', 'actualGoliveDate']);

  if (
    hasInputValue(payload.estimatedStartDate)
    && hasInputValue(payload.estimatedGoliveDate)
    && isValidPartnerDateInput(payload.estimatedStartDate)
    && isValidPartnerDateInput(payload.estimatedGoliveDate)
    && payload.estimatedGoliveDate < payload.estimatedStartDate
  ) {
    errors.estimatedGoliveDate = 'Go-live date must be on or after start date';
  }

  if (
    hasInputValue(payload.actualStartDate)
    && hasInputValue(payload.actualGoliveDate)
    && isValidPartnerDateInput(payload.actualStartDate)
    && isValidPartnerDateInput(payload.actualGoliveDate)
    && payload.actualGoliveDate < payload.actualStartDate
  ) {
    errors.actualGoliveDate = 'Actual go-live must be on or after actual start';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validatePAEProjectUpdatePayload = (payload) => {
  const base = validatePartnerProjectUpdatePayload(payload);
  const errors = { ...base.errors };

  ['partnerName', 'csn', 'companyName'].forEach((field) => {
    if (!hasInputValue(payload[field])) {
      errors[field] = 'This field is required';
    }
  });

  const partnerEmailError = getProjectLeadEmailError(payload.partnerEmail);
  if (partnerEmailError) {
    errors.partnerEmail = partnerEmailError;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const shouldPromptForProjectLeadEmailCorrection = (email) => (
  hasInputValue(email) && !isValidProjectLeadEmail(email)
);
